import React, { useState } from 'react';
import { 
  Box, 
  Flex, 
  VStack, 
  Text, 
  useColorMode, 
  Icon,
  HStack
} from '@chakra-ui/react';
import { useLocation, Link } from 'react-router-dom';
import { 
  FiHome, 
  FiTrendingUp, 
  FiSettings, 
  FiFileText
} from 'react-icons/fi';

import NavItem from './NavItem';
import UserMenu from './UserMenu';
import MoneyRain from './MoneyRain';
import { useAuthContext } from '../context/AuthContext';

// Helper function to get page title in proper case
const getPageTitle = (pathname) => {
  if (pathname === '/') return 'Dashboard';
  
  // Remove leading slash and replace hyphens with spaces
  const pageName = pathname.substring(1).replace(/-/g, ' ');
  
  // Convert to Title Case (capitalize first letter of each word)
  return pageName.split(' ')
    .map(word => {
      // Handle common acronyms
      if (word.toLowerCase() === 'api') return 'API';
      if (word.toLowerCase() === 'ui') return 'UI';
      if (word.toLowerCase() === 'http') return 'HTTP';
      // Normal title case for other words
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const Layout = ({ children }) => {
 
  const location = useLocation();
  const { user, logout } = useAuthContext();
  
  // Get properly formatted page title
  const pageTitle = getPageTitle(location.pathname);
  
  return (
    <Box minH="100vh" position="relative" className="layout-root">
      {/* Background Money Rain Effect */}
      <MoneyRain />
      
      <Flex minH="100vh" height="auto" overflowY="visible" className="layout-container">
        {/* Sidebar - Desktop */}
        <Box
          display={{ base: 'none', md: 'flex' }}
          flexDir="column"
          w="16"
          h="100vh"
          pos="fixed"
          borderRight="1px solid"
          borderColor="whiteAlpha.100"
          bg="blackAlpha.400"
          backdropFilter="blur(10px)"
          zIndex="10"
        >
          {/* Logo */}
          <Flex alignItems="center" justifyContent="center" h="16" position="relative">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Box
                className="crypto-icon animate-float"
                bgGradient="linear(135deg, rgb(40 185 16), rgb(24 79 38))"
                p="2"
                borderRadius="lg"
                w="9"
                h="9"
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                overflow="hidden"
                _hover={{ transform: 'scale(1.05)' }}
                transition="transform 0.3s"
                cursor="pointer"
              >
                <img 
                  src="/assets/light_logo.png" 
                  alt="WellSpire" 
                  style={{
                    width: '28px',
                    height: '28px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Link>
          </Flex>
          
          {/* Nav Items */}
          <VStack spacing={1} align="center" flex="1" py="4">
            <NavItem 
              icon={<FiHome />} 
              to="/" 
              tooltip="Dashboard" 
            />
            <NavItem 
              icon={<FiTrendingUp />} 
              to="/performance" 
              tooltip="Performance" 
            />
            <NavItem 
              icon={<FiSettings />} 
              to="/api-settings" 
              tooltip="API Settings" 
            />
            <NavItem 
              icon={<FiFileText />} 
              to="/trade-logs" 
              tooltip="Trade Logs" 
            />
          </VStack>
        </Box>
    
        
        {/* Main Content */}
      <Box 
  ml={{ base: 0, md: 16 }} 
  w="full"
  minH="100vh"
  height="auto"
  overflowY="visible"
  className="main-content-area"
  pb={{ base: "calc(100px + env(safe-area-inset-bottom))", md: "40px" }}  // ✅ iPhone için artırıldı
>
          {/* Header - With proper Title Case page names */}
          <Flex
            as="header"
            position="fixed"
            top={0}
            left={{ base: 0, md: "4rem" }}
            right={0}
            zIndex={50}
            h="14"
            alignItems="center"
            px={4}
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            backdropFilter="blur(10px)"
            bg="blackAlpha.800"
            width={{ base: "100%", md: "calc(100% - 4rem)" }}
          >
            <Flex align="center" justify="space-between" w="full">
              <Flex align="center" gap={3}>
                {/* Mobile Menu Button */}
               
                
                {/* Mobile Logo */}
                <Box display={{ base: 'flex', md: 'none' }} mr={2}>
                  <Link to="/" style={{ textDecoration: 'none' }}>
                    <Box
                      bgGradient="linear(135deg, rgb(40 185 16), rgb(24 79 38))"
                      p={1.5}
                      borderRadius="lg"
                      w={7}
                      h={7}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      _hover={{ transform: 'scale(1.05)' }}
                      transition="transform 0.3s"
                      cursor="pointer"
                    >
                      <img 
                        src="/assets/light_logo.png" 
                        alt="WellSpire" 
                        style={{
                          width: '20px',
                          height: '20px',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  </Link>
                </Box>
                
                {/* Page title with proper Title Case formatting */}
                <HStack spacing={1.5}>
                  <Text fontSize="sm" fontWeight="medium" color="white">AI</Text>
                  <Text color="gray.500">/</Text>
                  <Text fontSize="sm" fontWeight="medium" color="crypto.green.400">
                    {pageTitle}
                  </Text>
                </HStack>
              </Flex>
              
              {/* Right side actions */}
              <Flex align="center" gap={3}>
                {/* Theme Toggle */}
               
                
                {/* User Menu */}
                <UserMenu user={user} logout={logout} />
              </Flex>
            </Flex>
          </Flex>
          
          {/* Page Content - Added extra padding-top to account for fixed header */}
          <Box 
            p={4} 
            pt="16" 
            maxW="7xl" 
            mx="auto"
            className="page-content-wrapper"
            minH="calc(100vh - 3.5rem)"
            height="auto"
            overflowY="visible"
          >
            {children}
          </Box>
          
          {/* Mobile Bottom Navigation with proper Title Case */}
          <Box 
  display={{ base: 'block', md: 'none' }}
  position="fixed"
  bottom={0}
  left={0}
  right={0}
  borderTop="1px solid"
  borderColor="rgb(0 255 31 / 33%)"
  backdropFilter="blur-lg"
  zIndex={40}
  bg="rgb(0 0 0)"
  pb="env(safe-area-inset-bottom)"  // ✅ iPhone için eklendi
  minH="calc(60px + env(safe-area-inset-bottom))"  // ✅ Minimum yükseklik eklendi
>
  <Flex justify="space-around" py={2} pb="calc(15px + env(safe-area-inset-bottom))"> 
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Box className={`flex flex-col items-center transition-colors ${location.pathname === '/' ? 'text-crypto-green-400' : 'text-gray-400 hover:text-white'} py={1}`}>
                  <Icon as={FiHome} mb={1} />
                  <Text fontSize="xs" fontWeight="medium">Dashboard</Text>
                </Box>
              </Link>
              
              <Link to="/performance" style={{ textDecoration: 'none' }}>
                <Box className={`flex flex-col items-center transition-colors ${location.pathname === '/performance' ? 'text-crypto-green-400' : 'text-gray-400 hover:text-white'} py={1}`}>
                  <Icon as={FiTrendingUp} mb={1} />
                  <Text fontSize="xs" fontWeight="medium">Performance</Text>
                </Box>
              </Link>
              
              <Link to="/api-settings" style={{ textDecoration: 'none' }}>
                <Box className={`flex flex-col items-center transition-colors ${location.pathname === '/api-settings' ? 'text-crypto-green-400' : 'text-gray-400 hover:text-white'} py={1}`}>
                  <Icon as={FiSettings} mb={1} />
                  <Text fontSize="xs" fontWeight="medium">API Settings</Text>
                </Box>
              </Link>
              
              <Link to="/trade-logs" style={{ textDecoration: 'none' }}>
                <Box className={`flex flex-col items-center transition-colors ${location.pathname === '/trade-logs' ? 'text-crypto-green-400' : 'text-gray-400 hover:text-white'} ,py={1}`}>
                  <Icon as={FiFileText} mb={1} />
                  <Text fontSize="xs" fontWeight="medium">Trade Logs</Text>
                </Box>
              </Link>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default Layout;